import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader, UserNameCell } from '@/components/core';
import { ResourceBreakdown } from '../../types/timesheet-manager.types';

const formatHours = (hours: number) => `${hours.toFixed(2)}h`;

const projectCount = (resource: ResourceBreakdown) =>
  resource.Customers.reduce((sum, customer) => sum + customer.Projects.length, 0);

const totalByField = (
  resource: ResourceBreakdown,
  field: 'Billable' | 'GeneralAdmin' | 'SalesAndMarketing'
) =>
  resource.Customers.reduce(
    (sum, customer) =>
      sum + customer.Projects.reduce((projectSum, project) => projectSum + project[field], 0),
    0
  );

interface CreateTimesheetRollupTableColumnsProps {
  t: (key: string) => string;
}

/**
 * The rollup is aggregated in the browser from every matching entry, so the table sorts and filters
 * the whole list itself (`matchesColumnFilter`); column ids need not be schema fields here.
 */
export const createTimesheetRollupTableColumns = ({
  t,
}: CreateTimesheetRollupTableColumnsProps): ColumnDef<ResourceBreakdown>[] => [
  {
    id: 'ResourceName',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('TIMESHEET_MANAGER_RESOURCE')} />
    ),
    accessorFn: (row) => row.ResourceName,
    cell: ({ row }) => (
      <UserNameCell name={row.original.ResourceName} email={row.original.ResourceEmail} />
    ),
  },
  {
    id: 'DepartmentName',
    // Card: under the employee's name. Filtered by the toolbar's department dropdown, not here.
    meta: { mobile: 'subtitle' },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEPARTMENT')} />,
    accessorFn: (row) => row.DepartmentName ?? '-',
  },
  {
    id: 'CustomerCount',
    meta: { filter: { type: 'number' } },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('TIMESHEET_MANAGER_CUSTOMERS')} />
    ),
    accessorFn: (row) => row.Customers.length,
  },
  {
    id: 'ProjectCount',
    meta: { filter: { type: 'number' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('PROJECTS')} />,
    accessorFn: (row) => projectCount(row),
  },
  {
    id: 'Billable',
    meta: { filter: { type: 'number' } },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('TIMESHEET_MANAGER_BILLABLE')} />
    ),
    accessorFn: (row) => totalByField(row, 'Billable'),
    cell: ({ row }) => <span>{formatHours(totalByField(row.original, 'Billable'))}</span>,
  },
  {
    id: 'GeneralAdmin',
    meta: { filter: { type: 'number' } },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('TIMESHEET_MANAGER_GENERAL_ADMIN')} />
    ),
    accessorFn: (row) => totalByField(row, 'GeneralAdmin'),
    cell: ({ row }) => <span>{formatHours(totalByField(row.original, 'GeneralAdmin'))}</span>,
  },
  {
    id: 'SalesAndMarketing',
    meta: { filter: { type: 'number' } },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('TIMESHEET_MANAGER_SALES_MARKETING')} />
    ),
    accessorFn: (row) => totalByField(row, 'SalesAndMarketing'),
    cell: ({ row }) => <span>{formatHours(totalByField(row.original, 'SalesAndMarketing'))}</span>,
  },
  {
    id: 'GrandTotal',
    // Card: top right, as the old portal's grand total.
    meta: { filter: { type: 'number' }, mobile: 'badge' },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('TIMESHEET_MANAGER_GRAND_TOTAL')} />
    ),
    accessorFn: (row) => row.GrandTotal,
    cell: ({ row }) => <span className="font-medium">{formatHours(row.original.GrandTotal)}</span>,
  },
];
